import React from "react";
import AddressSearch from "react-loqate";
import "react-loqate/dist/react-loqate.cjs.development.css";
import "./quote.css";
import { BrandHeader, PageHero } from "./BrandChrome";
import OpeningHoursLocationPicker from "./OpeningHoursLocationPicker";
import BasketCustomGate, { isCustomGateItem } from "./BasketCustomGate";
import "./openingHours.css";

const loqateApiKey =
  process.env.REACT_APP_LOQATE_API_KEY || "AT25-NX49-XU13-KB17";

const branches = [
  {
    code: "00",
    name: "Burton",
    telephone: "01283 743010",
    address: "Wellington Road, Burton-upon-Trent, Staffordshire",
    postcode: "DE14 2AA",
  },
  {
    code: "01",
    name: "Rainham",
    telephone: "01708 253545",
    address: "24-36 Lamson Road, Ferry Lane North, Rainham, Essex",
    postcode: "RM13 9YY",
  },
  {
    code: "02",
    name: "Ilkeston",
    telephone: "0115 9302070",
    address: "Condor Road, Quarry Hill Industrial Estate, Ilkeston, Derbyshire",
    postcode: "DE7 4RE",
  },
  {
    code: "03",
    name: "Southampton",
    telephone: "02380 703333",
    address: "502 Millbrook Road, Third Avenue, Millbrook, Southampton",
    postcode: "SO15 0JX",
  },
  {
    code: "04",
    name: "Haydock",
    telephone: "01942 868888",
    address: "Unit 8 Haydock Lane, Haydock Industrial Estate, Haydock, St Helens",
    postcode: "WA11 9XE",
  },
  {
    code: "05",
    name: "Birmingham",
    telephone: "0121 5658282",
    address: "81-82 Middlemore Industrial Estate, Middlemore Road, Smethwick, Birmingham",
    postcode: "B66 2EP",
  },
  {
    code: "07",
    name: "Glasgow",
    telephone: "0141 7736699",
    address: "Dunivaig Road, Queenslie Industrial Estate, Queenslie, Glasgow, Scotland",
    postcode: "G33 4TP",
  },
  {
    code: "08",
    name: "Newport",
    telephone: "029 2280 1180",
    address: "Chartist Way, Rogerstone, Newport, Wales",
    postcode: "NP10 9XJ",
  },
];

const formatMoney = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

const hasMoneyValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  return Number.isFinite(Number.parseFloat(value));
};

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const isAddressComplete = (address) =>
  ["name", "addr1", "town", "postcode", "country"].every((field) =>
    String(address?.[field] || "").trim()
  );

const getTotalQuantity = (items = []) =>
  items.reduce((total, item) => {
    const quantity = Number.parseFloat(item.quantity ?? item.qty);
    return total + (Number.isFinite(quantity) ? Math.max(quantity, 0) : 0);
  }, 0);

const DiscountedPrice = ({ original, discounted, showDiscount }) => {
  const hasUsableDiscount = showDiscount && hasMoneyValue(discounted);

  return (
    <>
    {hasUsableDiscount ? (
      <>
        <span className="basket-price-original">£{formatMoney(original)}</span>
        <strong className="basket-price-discount">£{formatMoney(discounted)}</strong>
      </>
    ) : (
      <>£{formatMoney(original)}</>
    )}
    </>
  );
};

const createEmptyAddress = () => ({
  name: "",
  addr1: "",
  addr2: "",
  addr3: "",
  town: "",
  postcode: "",
  country: "GB",
});

const IntegratedAddressSearch = ({
  idPrefix,
  addressLine,
  onChange,
  onAddressSelect,
  required,
}) => {
  const addressLineRef = React.useRef(addressLine);
  const onChangeRef = React.useRef(onChange);
  addressLineRef.current = addressLine;
  onChangeRef.current = onChange;

  const IntegratedLoqateInput = React.useMemo(
    () =>
      ({ onChange: onLoqateChange, ...props }) => (
        <input
          {...props}
          id={`${idPrefix}-addr1`}
          type="search"
          aria-label="Address line 1"
          placeholder="Start typing an address or postcode"
          autoComplete="off"
          required={required}
          value={addressLineRef.current}
          onChange={(event) => {
            onChangeRef.current("addr1", event.target.value);
            onLoqateChange(event);
          }}
        />
      ),
    [idPrefix, required]
  );

  return (
    <AddressSearch
      locale="en-GB"
      apiKey={loqateApiKey}
      countries={["GB"]}
      limit={8}
      debounce={180}
      onSelect={onAddressSelect}
      components={{ Input: IntegratedLoqateInput }}
      classes={{
        input: "basket-loqate-input",
        list: "basket-loqate-list",
        listItem: "basket-loqate-list-item",
      }}
    />
  );
};

export const AddressFields = ({
  idPrefix,
  value,
  onChange,
  onAddressSelect,
  onPostcodeBlur,
  required = false,
}) => (
  <div className="basket-address-fields">
    <label className="basket-field basket-field--wide" htmlFor={`${idPrefix}-name`}>
      <span>
        Name or company {required && <b className="basket-required-mark" aria-hidden="true">*</b>}
      </span>
      <input
        id={`${idPrefix}-name`}
        type="text"
        autoComplete={`${idPrefix === "invoice" ? "billing" : "shipping"} name`}
        required={required}
        value={value.name}
        onChange={(event) => onChange("name", event.target.value)}
      />
    </label>

    <div className="basket-field basket-field--wide basket-address-search">
      <span>
        Address line 1 {required && <b className="basket-required-mark" aria-hidden="true">*</b>}
      </span>
      <IntegratedAddressSearch
        idPrefix={idPrefix}
        addressLine={value.addr1}
        onChange={onChange}
        onAddressSelect={onAddressSelect}
        required={required}
      />
    </div>

    <label className="basket-field basket-field--wide" htmlFor={`${idPrefix}-addr2`}>
      <span>Address line 2</span>
      <input
        id={`${idPrefix}-addr2`}
        type="text"
        autoComplete={`${idPrefix === "invoice" ? "billing" : "shipping"} address-line2`}
        value={value.addr2}
        onChange={(event) => onChange("addr2", event.target.value)}
      />
    </label>

    <label className="basket-field basket-field--wide" htmlFor={`${idPrefix}-addr3`}>
      <span>Address line 3</span>
      <input
        id={`${idPrefix}-addr3`}
        type="text"
        autoComplete={`${idPrefix === "invoice" ? "billing" : "shipping"} address-line3`}
        value={value.addr3}
        onChange={(event) => onChange("addr3", event.target.value)}
      />
    </label>

    <label className="basket-field" htmlFor={`${idPrefix}-town`}>
      <span>
        Town or city {required && <b className="basket-required-mark" aria-hidden="true">*</b>}
      </span>
      <input
        id={`${idPrefix}-town`}
        type="text"
        autoComplete={`${idPrefix === "invoice" ? "billing" : "shipping"} address-level2`}
        required={required}
        value={value.town}
        onChange={(event) => onChange("town", event.target.value)}
      />
    </label>

    <label className="basket-field" htmlFor={`${idPrefix}-postcode`}>
      <span>
        Postcode {required && <b className="basket-required-mark" aria-hidden="true">*</b>}
      </span>
      <input
        id={`${idPrefix}-postcode`}
        type="text"
        autoComplete={`${idPrefix === "invoice" ? "billing" : "shipping"} postal-code`}
        required={required}
        value={value.postcode}
        onChange={(event) => onChange("postcode", event.target.value.toUpperCase())}
        onBlur={() => onPostcodeBlur?.(value.postcode)}
      />
    </label>

    <label className="basket-field basket-field--wide" htmlFor={`${idPrefix}-country`}>
      <span>
        Country {required && <b className="basket-required-mark" aria-hidden="true">*</b>}
      </span>
      <input
        id={`${idPrefix}-country`}
        type="text"
        autoComplete={`${idPrefix === "invoice" ? "billing" : "shipping"} country`}
        required={required}
        value={value.country}
        onChange={(event) => onChange("country", event.target.value.toUpperCase())}
      />
    </label>
  </div>
);

export default class Basket extends React.Component {
  constructor(props) {
    super(props);

    const params = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
    const initialBasketId = (
      params.get("ref") || params.get("basket_id") || ""
    ).trim();
    const initialCoupon = (params.get("coupon") || "").trim();

    this.state = {
      basketId: initialBasketId,
      basket: null,
      error: null,
      couponCode: initialCoupon,
      appliedCoupon: initialCoupon,
      isLoading: Boolean(initialBasketId),
      isRefreshing: false,
      fulfilment: "collection",
      selectedBranch: "01",
      deliveryPreference: "all_together",
      deliveryMatchesInvoice: true,
      invoiceAddress: createEmptyAddress(),
      deliveryAddress: createEmptyAddress(),
      deliveryLocation: null,
      deliveryNotes: "",
      orderReference: "",
      emailAddress: "",
      mobileNumber: "",
      checkoutMode: "",
      accountStep: "email",
      accountEmail: "",
      accountCode: "",
      accountStatus: "",
      termsAccepted: false,
      showDebugModal: false,
      debugPayload: null,
      debugCopied: false,
      isLocatingPostcode: false,
      locationError: "",
    };

    this.basketRequestId = 0;
  }

  componentDidMount() {
    const { basketId, couponCode } = this.state;
    if (basketId) this.fetchBasket(basketId, couponCode);
  }

  handleInputChange = (event) => {
    this.setState({ basketId: event.target.value });
  };

  handleCouponChange = (event) => {
    this.setState({ couponCode: event.target.value });
  };

  chooseGuestCheckout = () => {
    this.setState({
      checkoutMode: "guest",
      accountStatus: "",
    });
  };

  chooseAccountCheckout = () => {
    this.setState({
      checkoutMode: "account",
      accountStep: "email",
      accountCode: "",
      accountStatus: "",
    });
  };

  resetCheckoutMode = () => {
    this.setState({
      checkoutMode: "",
      accountStep: "email",
      accountCode: "",
      accountStatus: "",
    });
  };

  handleAccountEmailSubmit = (event) => {
    event.preventDefault();
    const accountEmail = this.state.accountEmail.trim();
    if (!isValidEmail(accountEmail)) return;

    this.setState({
      accountEmail,
      emailAddress: accountEmail,
      accountStep: "code",
      accountCode: "",
      accountStatus:
        "The code-entry screen is ready. No email has been sent until the account authentication endpoint is connected.",
    });
  };

  handleAccountCodeSubmit = (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(this.state.accountCode)) return;

    this.setState({
      accountStatus:
        "The six-digit code is ready to verify against the account authentication endpoint. No sign-in has been completed yet.",
    });
  };

  handleAddressChange = (addressType, field, value) => {
    this.setState((currentState) => ({
      [addressType]: {
        ...currentState[addressType],
        [field]: value,
      },
    }));
  };

  handleFulfilmentChange = (event) => {
    const fulfilment = event.target.value;
    this.setState({ fulfilment }, () => {
      const { basket, selectedBranch } = this.state;
      if (!basket) return;

      if (fulfilment === "collection") {
        this.requestBasketPricing("col", selectedBranch);
        return;
      }

      const postcode = this.getEffectiveDeliveryAddress().postcode.trim();
      if (postcode) {
        this.refreshDelivery(postcode);
      } else {
        this.requestBasketPricing("del", "");
      }
    });
  };

  getEffectiveDeliveryAddress = () =>
    this.state.deliveryMatchesInvoice
      ? this.state.invoiceAddress
      : this.state.deliveryAddress;

  getFulfilmentQuery = () => {
    if (this.state.fulfilment === "delivery") {
      const postcode = this.getEffectiveDeliveryAddress().postcode.trim();
      return { deltype: "del", postcode };
    }

    return {
      deltype: "col",
      postcode: this.state.selectedBranch,
    };
  };

  requestBasketPricing = (deltype, postcode) => {
    const pricingPostcode = String(postcode || "").trim();
    if (!this.state.basket || (deltype !== "del" && !pricingPostcode)) {
      return false;
    }

    this.fetchBasket(this.state.basketId, this.state.appliedCoupon, {
      deltype,
      postcode: pricingPostcode,
    });
    return true;
  };

  normaliseLoqateAddress = (address, currentAddress) => ({
    ...currentAddress,
    name: address.Company || currentAddress.name,
    addr1: address.Line1 || "",
    addr2: address.Line2 || "",
    addr3: address.Line3 || "",
    town: address.City || "",
    postcode: (address.PostalCode || "").toUpperCase(),
    country: address.CountryIso2 || "GB",
  });

  handleAddressSelect = (addressType, address) => {
    const nextAddress = this.normaliseLoqateAddress(
      address,
      this.state[addressType]
    );

    this.setState({ [addressType]: nextAddress }, () => {
      const affectsDelivery =
        this.state.fulfilment === "delivery" &&
        (addressType === "deliveryAddress" ||
          (addressType === "invoiceAddress" && this.state.deliveryMatchesInvoice));

      if (!affectsDelivery || !nextAddress.postcode) return;

      const latitude =
        address.Latitude === "" || address.Latitude === null || address.Latitude === undefined
          ? Number.NaN
          : Number(address.Latitude);
      const longitude =
        address.Longitude === "" || address.Longitude === null || address.Longitude === undefined
          ? Number.NaN
          : Number(address.Longitude);
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        this.setState({
          deliveryLocation: { latitude, longitude },
          locationError: "",
        });
      }
      this.refreshDelivery(nextAddress.postcode, {
        latitude,
        longitude,
      });
    });
  };

  handleDeliveryPostcodeBlur = (addressType, postcode) => {
    const affectsDelivery =
      this.state.fulfilment === "delivery" &&
      (addressType === "deliveryAddress" ||
        (addressType === "invoiceAddress" && this.state.deliveryMatchesInvoice));

    if (affectsDelivery && postcode.trim()) this.refreshDelivery(postcode);
  };

  handleBranchChange = (event) => {
    const selectedBranch = event.target.value;
    this.setState({ selectedBranch }, () => {
      this.requestBasketPricing("col", selectedBranch);
    });
  };

  handleDeliveryMatchesInvoice = (event) => {
    const deliveryMatchesInvoice = event.target.checked;
    this.setState({ deliveryMatchesInvoice }, () => {
      if (this.state.fulfilment !== "delivery") return;
      const postcode = this.getEffectiveDeliveryAddress().postcode.trim();
      if (postcode) {
        this.refreshDelivery(postcode);
      } else {
        this.setState({ deliveryLocation: null, locationError: "" });
        this.requestBasketPricing("del", "");
      }
    });
  };

  locatePostcode = async (postcode) => {
    const normalisedPostcode = postcode.replace(/\s+/g, "").toUpperCase();
    if (!normalisedPostcode) return;

    this.setState({ isLocatingPostcode: true, locationError: "" });
    try {
      const response = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(normalisedPostcode)}`
      );
      if (!response.ok) throw new Error("Postcode not found");
      const data = await response.json();
      const latitude = Number(data?.result?.latitude);
      const longitude = Number(data?.result?.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error("Location unavailable");
      }
      this.setState({
        deliveryLocation: { latitude, longitude },
        isLocatingPostcode: false,
        locationError: "",
      });
    } catch (error) {
      this.setState({
        isLocatingPostcode: false,
        locationError:
          "We could not place that postcode automatically. You can still click the map to set the pin.",
      });
    }
  };

  refreshDelivery = (postcode, loqateLocation = null) => {
    if (
      !Number.isFinite(loqateLocation?.latitude) ||
      !Number.isFinite(loqateLocation?.longitude)
    ) {
      this.locatePostcode(postcode);
    }

    this.requestBasketPricing("del", postcode);
  };

  updateBrowserUrl = (basketId, couponCode = "") => {
    const url = new URL(window.location.href);
    url.searchParams.set("ref", basketId);
    url.searchParams.delete("basket_id");

    if (couponCode) {
      url.searchParams.set("coupon", couponCode);
    } else {
      url.searchParams.delete("coupon");
    }

    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  };

  handleApplyCoupon = () => {
    const basketId = this.state.basketId.trim();
    const appliedCoupon = this.state.couponCode.trim();

    if (basketId && appliedCoupon) {
      this.setState({ appliedCoupon }, () => {
        this.updateBrowserUrl(basketId, appliedCoupon);
        this.fetchBasket(basketId, appliedCoupon);
      });
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const basketId = this.state.basketId.trim();

    if (basketId) {
      this.setState({ basketId });
      const couponCode = this.state.couponCode.trim();
      this.updateBrowserUrl(basketId, couponCode);
      this.fetchBasket(basketId, couponCode);
    }
  };

  fetchBasket = async (basketId, couponCode = "", fulfilmentQuery = null) => {
    const { deltype, postcode } = fulfilmentQuery || this.getFulfilmentQuery();
    const url =
      "https://appapi.fhbrundle.co.uk/api?cmd=basket_share" +
      `&basket_id=${encodeURIComponent(basketId)}` +
      `&postcode=${encodeURIComponent(postcode)}&deltype=${encodeURIComponent(deltype)}&price` +
      `&coupon=${encodeURIComponent(couponCode)}`;

    const requestId = ++this.basketRequestId;
    this.setState((currentState) => ({
      isLoading: !currentState.basket,
      isRefreshing: Boolean(currentState.basket),
      error: null,
    }));

    try {
      const response = await fetch(url, { cache: "no-store" });
      const data = await response.json();
      if (requestId !== this.basketRequestId) return;

      if (data && Array.isArray(data.basket) && data.basket.length > 0) {
        this.setState({
          basket: data,
          error: null,
          isLoading: false,
          isRefreshing: false,
        });
      } else {
        this.setState({
          basket: null,
          error: "We could not find any items in that basket.",
          isLoading: false,
          isRefreshing: false,
        });
      }
    } catch (error) {
      console.error("Basket fetch failed:", error);
      if (requestId !== this.basketRequestId) return;
      this.setState({
        basket: this.state.basket,
        error: "We could not load that basket. Please try again.",
        isLoading: false,
        isRefreshing: false,
      });
    }
  };

  openBasket = () => {
    const { basket, appliedCoupon } = this.state;
    const baseUrl = `https://www.fhbrundle.co.uk/basket?loadBasket=${encodeURIComponent(
      basket.basket_id
    )}`;
    const coupon = appliedCoupon.trim();

    window.location.href = coupon
      ? `${baseUrl}&discount=${encodeURIComponent(coupon)}`
      : baseUrl;
  };

  buildPaymentPayload = () => {
    const {
      basket,
      fulfilment,
      selectedBranch,
      deliveryPreference,
      invoiceAddress,
      deliveryLocation,
      deliveryNotes,
      orderReference,
      emailAddress,
      mobileNumber,
      checkoutMode,
      accountEmail,
      termsAccepted,
      appliedCoupon,
    } = this.state;
    const isDelivery = fulfilment === "delivery";
    const offersSplitDelivery =
      isDelivery && getTotalQuantity(basket.basket) > 1;
    const collectionBranch = branches.find(
      ({ code }) => code === selectedBranch
    );
    const deliveryAddress = this.getEffectiveDeliveryAddress();

    return {
      basket_id: basket.basket_id,
      created_at: new Date().toISOString(),
      your_order_reference: orderReference.trim().slice(0, 20) || null,
      checkout: {
        mode: checkoutMode || null,
        account_email:
          checkoutMode === "account" ? accountEmail.trim() || null : null,
      },
      contact: {
        email: emailAddress.trim(),
        mobile: mobileNumber.trim() || null,
      },
      invoice_address: { ...invoiceAddress },
      fulfilment: {
        type: fulfilment,
        deltype: isDelivery ? "del" : "col",
        postcode: isDelivery
          ? deliveryAddress.postcode.trim()
          : selectedBranch,
        collection_branch: isDelivery ? null : collectionBranch,
        delivery_address: isDelivery ? { ...deliveryAddress } : null,
        delivery_location: isDelivery ? deliveryLocation : null,
        delivery_notes: isDelivery ? deliveryNotes.trim() || null : null,
        delivery_preference: offersSplitDelivery ? deliveryPreference : null,
        allow_split_delivery: offersSplitDelivery
          ? deliveryPreference === "split_allowed"
          : false,
      },
      terms: {
        accepted: termsAccepted,
        accepted_at: new Date().toISOString(),
        url: "https://www.fhbrundle.co.uk/company/legal",
      },
      coupon: appliedCoupon || null,
      pricing: {
        sub_total: basket.sub_total,
        carriage: basket.carrage ?? basket.carriage ?? 0,
        vat: basket.vat,
        total_incl_vat: basket.total_incl_vat,
        coupon_info: basket.coupon_info || {},
      },
      items: basket.basket,
    };
  };

  handleMakePayment = () => {
    const invoiceAddressComplete = isAddressComplete(this.state.invoiceAddress);
    const deliveryAddressComplete =
      this.state.fulfilment !== "delivery" ||
      isAddressComplete(this.getEffectiveDeliveryAddress());

    if (
      !invoiceAddressComplete ||
      !deliveryAddressComplete ||
      !isValidEmail(this.state.emailAddress) ||
      !this.state.termsAccepted
    ) {
      return;
    }

    this.setState({
      debugPayload: this.buildPaymentPayload(),
      showDebugModal: true,
      debugCopied: false,
    });
  };

  copyDebugPayload = async () => {
    if (!this.state.debugPayload) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(this.state.debugPayload, null, 2)
      );
      this.setState({ debugCopied: true });
    } catch (error) {
      console.error("Could not copy payment payload:", error);
    }
  };

  renderCouponMessage() {
    const { appliedCoupon, basket } = this.state;
    if (!appliedCoupon || !basket?.coupon_info) return null;

    const { valid, eligible, description } = basket.coupon_info;

    if (valid && eligible) {
      return (
        <div className="basket-coupon-message is-success" role="status">
          <strong>Discount “{appliedCoupon}” applied.</strong>
          {description && <span>{description}</span>}
        </div>
      );
    }

    if (valid && eligible === false) {
      return (
        <div className="basket-coupon-message is-warning" role="status">
          <strong>That discount is not available for this basket.</strong>
          {description && <span>{description}</span>}
        </div>
      );
    }

    if (valid === false) {
      return (
        <div className="basket-coupon-message is-error" role="status">
          <strong>“{appliedCoupon}” is not a valid discount code.</strong>
        </div>
      );
    }

    return null;
  }

  renderCheckoutGateway() {
    const {
      checkoutMode,
      accountStep,
      accountEmail,
      accountCode,
      accountStatus,
    } = this.state;

    if (checkoutMode === "guest") {
      return (
        <section className="basket-checkout-gateway is-complete">
          <div>
            <span className="basket-panel-eyebrow">Checkout</span>
            <h2>Checking out as guest</h2>
            <p>You can continue without signing in to an account.</p>
          </div>
          <button
            type="button"
            className="button-secondary basket-checkout-change"
            onClick={this.resetCheckoutMode}
          >
            Change
          </button>
        </section>
      );
    }

    if (checkoutMode === "account") {
      return (
        <section
          className="basket-checkout-gateway basket-account-signin"
          aria-labelledby="basket-account-signin-title"
        >
          <div className="basket-checkout-gateway__heading">
            <div>
              <span className="basket-panel-eyebrow">Account checkout</span>
              <h2 id="basket-account-signin-title">Sign in with your email</h2>
              <p>
                We’ll use a secure six-digit code so you do not need to remember a
                password.
              </p>
            </div>
            <button
              type="button"
              className="button-secondary basket-checkout-change"
              onClick={this.resetCheckoutMode}
            >
              Back
            </button>
          </div>

          {accountStep === "email" ? (
            <form
              className="basket-account-form"
              onSubmit={this.handleAccountEmailSubmit}
            >
              <label className="basket-field" htmlFor="account-email">
                <span>Email address</span>
                <input
                  id="account-email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="name@example.com"
                  value={accountEmail}
                  onChange={(event) =>
                    this.setState({ accountEmail: event.target.value })
                  }
                />
              </label>
              <button
                type="submit"
                className="pay_button"
                disabled={!isValidEmail(accountEmail)}
              >
                Send six-digit code
              </button>
            </form>
          ) : (
            <form
              className="basket-account-code-form"
              onSubmit={this.handleAccountCodeSubmit}
            >
              <div className="basket-account-email-summary">
                <span>Code for</span>
                <strong>{accountEmail}</strong>
                <button
                  type="button"
                  onClick={() =>
                    this.setState({
                      accountStep: "email",
                      accountCode: "",
                      accountStatus: "",
                    })
                  }
                >
                  Edit email
                </button>
              </div>
              <label className="basket-field" htmlFor="account-code">
                <span>Six-digit sign-in code</span>
                <input
                  id="account-code"
                  className="basket-account-code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength="6"
                  required
                  placeholder="000000"
                  value={accountCode}
                  onChange={(event) =>
                    this.setState({
                      accountCode: event.target.value.replace(/\D/g, "").slice(0, 6),
                    })
                  }
                />
              </label>
              <button
                type="submit"
                className="pay_button"
                disabled={!/^\d{6}$/.test(accountCode)}
              >
                Verify code
              </button>
            </form>
          )}

          {accountStatus && (
            <p className="basket-account-status" role="status">
              {accountStatus}
            </p>
          )}

          <button
            type="button"
            className="basket-account-guest-link"
            onClick={this.chooseGuestCheckout}
          >
            Checkout as guest instead
          </button>
        </section>
      );
    }

    return (
      <section
        className="basket-checkout-gateway"
        aria-labelledby="basket-checkout-choice-title"
      >
        <div className="basket-checkout-gateway__heading">
          <div>
            <span className="basket-panel-eyebrow">Checkout</span>
            <h2 id="basket-checkout-choice-title">How would you like to checkout?</h2>
            <p>Continue as a guest or sign in securely with your account email.</p>
          </div>
        </div>

        <div className="basket-checkout-mode-options">
          <button
            type="button"
            className="basket-checkout-mode-option"
            onClick={this.chooseGuestCheckout}
          >
            <span className="basket-checkout-mode-option__icon" aria-hidden="true">
              G
            </span>
            <span>
              <strong>Checkout as guest</strong>
              <small>Continue without signing in.</small>
            </span>
          </button>
          <button
            type="button"
            className="basket-checkout-mode-option"
            onClick={this.chooseAccountCheckout}
          >
            <span className="basket-checkout-mode-option__icon" aria-hidden="true">
              A
            </span>
            <span>
              <strong>I have an account</strong>
              <small>Sign in using a six-digit code sent by email.</small>
            </span>
          </button>
        </div>
      </section>
    );
  }

  render() {
    const { basketId, basket, error, isLoading, isRefreshing } = this.state;
    const hasDiscount = Boolean(
      basket?.coupon_info?.valid && basket?.coupon_info?.eligible !== false
    );
    const totalQuantity = getTotalQuantity(basket?.basket);
    const offersSplitDelivery =
      this.state.fulfilment === "delivery" && totalQuantity > 1;
    const showCheckoutDetails =
      this.state.checkoutMode === "guest" ||
      (this.state.checkoutMode === "account" &&
        this.state.accountStep === "verified");
    const invoiceAddressComplete = isAddressComplete(
      this.state.invoiceAddress
    );
    const deliveryAddressComplete =
      this.state.fulfilment !== "delivery" ||
      isAddressComplete(this.getEffectiveDeliveryAddress());
    const canContinue =
      showCheckoutDetails &&
      invoiceAddressComplete &&
      deliveryAddressComplete &&
      isValidEmail(this.state.emailAddress) &&
      this.state.termsAccepted;

    return (
      <div className="App app-shell basket-page">
        <BrandHeader title="Basket review" subtitle="Continue your order online" />
        {!basket && (
          <PageHero
            title="Review your basket"
            description="Enter your basket code to review the items and continue editing your order online."
          >
            <form className="lookup-form lookup-form--inline" onSubmit={this.handleSubmit}>
              <input
                type="text"
                placeholder="Enter basket code"
                aria-label="Enter basket code"
                value={basketId}
                onChange={this.handleInputChange}
                className="form_input"
              />
              <button type="submit" className="lookup_link" disabled={isLoading}>
                {isLoading ? "Loading…" : "Find basket"}
              </button>
            </form>
          </PageHero>
        )}

        {isLoading && (
          <div className="basket-loading" role="status" aria-live="polite">
            <span className="basket-loading__spinner" aria-hidden="true" />
            <div>
              <strong>Loading your basket</strong>
              <span>Retrieving the latest products and prices…</span>
            </div>
          </div>
        )}

        {!isLoading && error && <div className="no_results_message">{error}</div>}

        {basket && (
          <main className="basket-content">
            <section className="basket-reference-card" aria-label="Basket summary">
              <div>
                <span>Basket reference</span>
                <strong>{basket.basket_id}</strong>
              </div>
              <div>
                <span>Items</span>
                <strong>{basket.basket.length}</strong>
              </div>
              {isRefreshing && (
                <div className="basket-refreshing" role="status">
                  <span className="basket-loading__spinner" aria-hidden="true" />
                  <strong>Updating pricing…</strong>
                </div>
              )}
            </section>

            <section className="ProductLanding quote line-items-panel basket-items-panel">
              <div className="ProductLandingGrid">
                {basket.basket.map((item, index) => {
                  if (isCustomGateItem(item)) {
                    return (
                      <BasketCustomGate
                        key={`${item.NS_ref || item.product}-${index}`}
                        item={item}
                      />
                    );
                  }

                  const productUrl = `https://www.fhbrundle.co.uk/products/${item.product}`;
                  const showUnitDiscount = Boolean(
                    item.discount_applies && item.discounted_unit_price
                  );
                  const showLineDiscount = Boolean(
                    item.discount_applies && item.discounted_line_total
                  );

                  return (
                    <article key={`${item.product}-${index}`} className="product_table prodlayoutdefault">
                      <div className="product_card_img_container">
                        <a className="product_card_link" href={productUrl}>
                          <img
                            className="product_card_img"
                            src={`https://samsara-web.s3-eu-west-1.amazonaws.com/webimages/${item.product}/${item.product}x140.jpg`}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src =
                                "data:image/svg+xml;base64," +
                                btoa(
                                  '<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"><rect width="1" height="1" fill="white"/></svg>'
                                );
                            }}
                            alt={item.title || item.product}
                          />
                        </a>
                      </div>

                      <div className="product_card_content">
                        <div className="product_card_desc">
                          <a className="product_card_link" href={productUrl}>
                            {item.title}
                          </a>
                        </div>
                        <div className="product_card_code">{item.product}</div>

                        {item.notepad && item.notepad !== "@@" && (
                          <div className="product_card_note">
                            {item.notepad.replaceAll("@", "")}
                          </div>
                        )}

                        <div className="product_card_bottom">
                          <div className="prod_price">
                            <div className="quote_item">
                              <div className="quote_label">Quantity</div>
                              <div className="quote_value">x{item.quantity}</div>
                            </div>
                            <div className="quote_item">
                              <div className="quote_label">Unit price</div>
                              <div className="quote_value">
                                <DiscountedPrice
                                  original={item.unit_price}
                                  discounted={item.discounted_unit_price}
                                  showDiscount={showUnitDiscount}
                                />
                              </div>
                            </div>
                            <div className="quote_item">
                              <div className="quote_label">Line total</div>
                              <div className="quote_value">
                                <DiscountedPrice
                                  original={item.line_price}
                                  discounted={item.discounted_line_total}
                                  showDiscount={showLineDiscount}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="basket-checkout-card">
              <div className="basket-discount-panel">
                <span className="basket-panel-eyebrow">Optional</span>
                <h2>Discount code</h2>
                <p>Apply a valid discount before continuing to your online basket.</p>
                <div className="basket-discount-controls">
                  <input
                    type="text"
                    id="couponInput"
                    aria-label="Discount code"
                    placeholder="Enter discount code"
                    className="form_input"
                    value={this.state.couponCode}
                    onChange={this.handleCouponChange}
                  />
                  <button
                    type="button"
                    className="discount_button"
                    onClick={this.handleApplyCoupon}
                    disabled={
                      isLoading || isRefreshing || !this.state.couponCode.trim()
                    }
                  >
                    Apply code
                  </button>
                </div>
                {this.renderCouponMessage()}
              </div>

              <div className="basket-totals" aria-label="Basket totals">
                <div className="basket-total-row">
                  <span>Subtotal (ex VAT)</span>
                  <div>
                    <DiscountedPrice
                      original={basket.sub_total}
                      discounted={basket.coupon_info?.new_total}
                      showDiscount={hasDiscount}
                    />
                  </div>
                </div>
                {hasMoneyValue(basket.carrage ?? basket.carriage) &&
                  Number.parseFloat(basket.carrage ?? basket.carriage) > 0 && (
                    <div className="basket-total-row">
                      <span>Delivery</span>
                      <div>£{formatMoney(basket.carrage ?? basket.carriage)}</div>
                    </div>
                  )}
                <div className="basket-total-row">
                  <span>VAT</span>
                  <div>
                    <DiscountedPrice
                      original={basket.vat}
                      discounted={basket.coupon_info?.new_vat}
                      showDiscount={hasDiscount}
                    />
                  </div>
                </div>
                <div className="basket-total-row basket-total-row--grand">
                  <span>Total (inc VAT)</span>
                  <div>
                    <DiscountedPrice
                      original={basket.total_incl_vat}
                      discounted={basket.coupon_info?.new_total_incvat}
                      showDiscount={hasDiscount}
                    />
                  </div>
                </div>
              </div>
            </section>

            {this.renderCheckoutGateway()}

            {showCheckoutDetails && (
              <>
                <section className="basket-address-card">
              <div className="basket-section-heading">
                <span className="basket-panel-eyebrow">Delivery details</span>
                <h2>How would you like to receive your order?</h2>
                <p>
                  Add the invoice address, then choose delivery or collection for
                  this basket.
                </p>
              </div>

              <div className="basket-fulfilment-options" role="radiogroup" aria-label="Order fulfilment">
                <label
                  className={`basket-fulfilment-option${
                    this.state.fulfilment === "delivery" ? " is-selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="fulfilment"
                    value="delivery"
                    checked={this.state.fulfilment === "delivery"}
                    onChange={this.handleFulfilmentChange}
                  />
                  <span>
                    <strong>Delivery</strong>
                    <small>Send the order to your chosen address.</small>
                  </span>
                </label>

                <label
                  className={`basket-fulfilment-option${
                    this.state.fulfilment === "collection" ? " is-selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="fulfilment"
                    value="collection"
                    checked={this.state.fulfilment === "collection"}
                    onChange={this.handleFulfilmentChange}
                  />
                  <span>
                    <strong>Collection</strong>
                    <small>Collect the order from F.H. Brundle.</small>
                  </span>
                </label>
              </div>

              <div className="basket-address-grid">
                <div className="basket-address-panel">
                  <div className="basket-address-panel__heading">
                    <div>
                      <span>Invoice address</span>
                      <h3>Billing details</h3>
                    </div>
                  </div>
                  <AddressFields
                    idPrefix="invoice"
                    required
                    value={this.state.invoiceAddress}
                    onChange={(field, value) =>
                      this.handleAddressChange("invoiceAddress", field, value)
                    }
                    onAddressSelect={(address) =>
                      this.handleAddressSelect("invoiceAddress", address)
                    }
                    onPostcodeBlur={(postcode) =>
                      this.handleDeliveryPostcodeBlur("invoiceAddress", postcode)
                    }
                  />
                </div>

                {this.state.fulfilment === "delivery" ? (
                  <div className="basket-address-panel">
                    <div className="basket-address-panel__heading">
                      <div>
                        <span>Delivery address</span>
                        <h3>Where should we deliver?</h3>
                      </div>
                      <label className="basket-address-copy-toggle">
                        <input
                          type="checkbox"
                          checked={this.state.deliveryMatchesInvoice}
                          onChange={this.handleDeliveryMatchesInvoice}
                        />
                        <span>Same as invoice</span>
                      </label>
                    </div>

                    {this.state.deliveryMatchesInvoice ? (
                      <div className="basket-address-match-note" role="status">
                        <span className="basket-address-match-note__icon" aria-hidden="true">
                          &#10003;
                        </span>
                        <div>
                          <strong>Using the invoice address</strong>
                          <span>Untick the option above to enter a different delivery address.</span>
                        </div>
                      </div>
                    ) : (
                      <AddressFields
                        idPrefix="delivery"
                        required
                        value={this.state.deliveryAddress}
                        onChange={(field, value) =>
                          this.handleAddressChange("deliveryAddress", field, value)
                        }
                        onAddressSelect={(address) =>
                          this.handleAddressSelect("deliveryAddress", address)
                        }
                        onPostcodeBlur={(postcode) =>
                          this.handleDeliveryPostcodeBlur("deliveryAddress", postcode)
                        }
                      />
                    )}

                    {offersSplitDelivery && (
                      <div
                        className="basket-delivery-preference"
                        role="radiogroup"
                        aria-labelledby="basket-delivery-preference-title"
                      >
                        <span
                          id="basket-delivery-preference-title"
                          className="basket-panel-eyebrow"
                        >
                          Delivery preference
                        </span>
                        <h4>How should we fulfil your order?</h4>
                        <div className="basket-delivery-preference__options">
                          <label
                            className={`basket-fulfilment-option${
                              this.state.deliveryPreference === "split_allowed"
                                ? " is-selected"
                                : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name="delivery-preference"
                              value="split_allowed"
                              checked={
                                this.state.deliveryPreference === "split_allowed"
                              }
                              onChange={(event) =>
                                this.setState({
                                  deliveryPreference: event.target.value,
                                })
                              }
                            />
                            <span>
                              <strong>
                                I’m happy for my order to be fulfilled in multiple deliveries
                              </strong>
                              <small>Available items may be sent separately.</small>
                            </span>
                          </label>

                          <label
                            className={`basket-fulfilment-option${
                              this.state.deliveryPreference === "all_together"
                                ? " is-selected"
                                : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name="delivery-preference"
                              value="all_together"
                              checked={
                                this.state.deliveryPreference === "all_together"
                              }
                              onChange={(event) =>
                                this.setState({
                                  deliveryPreference: event.target.value,
                                })
                              }
                            />
                            <span>
                              <strong>Deliver it all together</strong>
                              <small>Wait until the complete order is ready.</small>
                            </span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="basket-delivery-location">
                      {!this.getEffectiveDeliveryAddress().postcode.trim() && (
                        <p className="basket-location-status is-prompt" role="status">
                          Enter or choose a delivery postcode to calculate delivery
                          and place the starting pin.
                        </p>
                      )}
                      {this.state.isLocatingPostcode && (
                        <p className="basket-location-status" role="status">
                          Finding the postcode on the map…
                        </p>
                      )}
                      {this.state.locationError && (
                        <p className="basket-location-status is-error" role="alert">
                          {this.state.locationError}
                        </p>
                      )}
                      <OpeningHoursLocationPicker
                        value={this.state.deliveryLocation}
                        onChange={(deliveryLocation) =>
                          this.setState({ deliveryLocation, locationError: "" })
                        }
                        legend="Exact delivery location"
                        description="We will place the pin from the postcode. Drag it or click the map to refine the delivery point."
                      />
                    </div>

                    <label className="basket-field basket-field--wide basket-delivery-notes">
                      <span>Delivery notes</span>
                      <textarea
                        rows="4"
                        placeholder="Add access details, unloading instructions or anything else our transport team should know."
                        value={this.state.deliveryNotes}
                        onChange={(event) =>
                          this.setState({ deliveryNotes: event.target.value })
                        }
                      />
                    </label>
                  </div>
                ) : (
                  <div className="basket-collection-panel">
                    <span className="basket-panel-eyebrow">Collection branch</span>
                    <h3>Where would you like to collect?</h3>
                    <label className="basket-branch-select" htmlFor="collection-branch">
                      <span>F.H. Brundle branch</span>
                      <select
                        id="collection-branch"
                        value={this.state.selectedBranch}
                        onChange={this.handleBranchChange}
                      >
                        {branches.map((branch) => (
                          <option value={branch.code} key={branch.code}>
                            {branch.name} — {branch.postcode}
                          </option>
                        ))}
                      </select>
                    </label>
                    {(() => {
                      const branch =
                        branches.find(
                          ({ code }) => code === this.state.selectedBranch
                        ) || branches[0];
                      return (
                        <address className="basket-branch-details">
                          <strong>{branch.name}</strong>
                          <span>{branch.address}</span>
                          <b>{branch.postcode}</b>
                          <a href={`tel:${branch.telephone.replace(/\s+/g, "")}`}>
                            {branch.telephone}
                          </a>
                        </address>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="basket-contact-panel">
                <div className="basket-contact-panel__heading">
                  <span className="basket-panel-eyebrow">Order confirmation</span>
                  <h3>Where should we send your confirmation?</h3>
                  <p>
                    We will use these details for updates relating to this order.
                  </p>
                </div>

                <div className="basket-contact-fields">
                  <label
                    className="basket-field basket-field--wide"
                    htmlFor="order-reference"
                  >
                    <span>Your order reference <em>Optional</em></span>
                    <input
                      id="order-reference"
                      type="text"
                      autoComplete="off"
                      maxLength="20"
                      placeholder="Project name, purchase order or internal reference"
                      value={this.state.orderReference}
                      onChange={(event) =>
                        this.setState({
                          orderReference: event.target.value.slice(0, 20),
                        })
                      }
                    />
                  </label>

                  <label className="basket-field" htmlFor="confirmation-email">
                    <span>Email address <b aria-hidden="true">*</b></span>
                    <input
                      id="confirmation-email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="name@example.com"
                      value={this.state.emailAddress}
                      onChange={(event) =>
                        this.setState({ emailAddress: event.target.value })
                      }
                    />
                  </label>

                  <label className="basket-field" htmlFor="confirmation-mobile">
                    <span>Mobile number <em>Optional</em></span>
                    <input
                      id="confirmation-mobile"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="e.g. 07123 456789"
                      value={this.state.mobileNumber}
                      onChange={(event) =>
                        this.setState({ mobileNumber: event.target.value })
                      }
                    />
                  </label>
                </div>

                <label className="basket-terms-option">
                  <input
                    type="checkbox"
                    required
                    checked={this.state.termsAccepted}
                    onChange={(event) =>
                      this.setState({ termsAccepted: event.target.checked })
                    }
                  />
                  <span>
                    I accept the{" "}
                    <a
                      href="https://www.fhbrundle.co.uk/company/legal"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms and conditions
                    </a>
                    .
                  </span>
                </label>
              </div>
                </section>

                <section className="basket-action-card">
              <div className="basket-action-buttons">
                <button
                  type="button"
                  className="pay_button basket-edit-order-button"
                  onClick={this.openBasket}
                >
                  Edit order on website
                </button>
                <button
                  type="button"
                  className="pay_button"
                  onClick={this.handleMakePayment}
                  disabled={!canContinue}
                  aria-describedby={!canContinue ? "basket-continue-requirements" : undefined}
                >
                  Make payment
                </button>
              </div>
              {!canContinue && (
                <p id="basket-continue-requirements" className="basket-action-requirements">
                  Complete the required invoice address
                  {this.state.fulfilment === "delivery" ? " and delivery address" : ""},
                  enter a valid email address and accept the terms and conditions to make payment.
                </p>
              )}
                </section>
              </>
            )}
          </main>
        )}

        {this.state.showDebugModal && this.state.debugPayload && (
          <div
            className="basket-debug-overlay"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                this.setState({ showDebugModal: false });
              }
            }}
          >
            <section
              className="basket-debug-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="basket-debug-title"
            >
              <div className="basket-debug-modal__heading">
                <div>
                  <span className="basket-panel-eyebrow">Debug preview</span>
                  <h2 id="basket-debug-title">Payment request JSON</h2>
                </div>
                <button
                  type="button"
                  className="basket-debug-modal__close"
                  aria-label="Close payment request preview"
                  onClick={() => this.setState({ showDebugModal: false })}
                >
                  &times;
                </button>
              </div>
              <p>
                This is the payload ready to send to the payment endpoint. Nothing
                has been posted yet.
              </p>
              <pre>{JSON.stringify(this.state.debugPayload, null, 2)}</pre>
              <div className="basket-debug-modal__actions">
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => this.setState({ showDebugModal: false })}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="pay_button"
                  onClick={this.copyDebugPayload}
                >
                  {this.state.debugCopied ? "Copied" : "Copy JSON"}
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
    );
  }
}
